import React from 'react';
import { Box, Text, Flex, Anchor } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleExclamation,
  faTriangleExclamation,
  faCircleInfo,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-solid-svg-icons';
import type { RequestTrackingData } from '../../../api/cmail.api';
import { escalationNote } from '../utils/trackingItemLabels';
import classes from './CmailStatusBanner.module.css';

const JIRA_BASE_URL = 'https://cowbell.atlassian.net/browse';

const formatCategoryName = (category: string) => {
  if (category === category.toUpperCase()) {
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  }

  return category
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getStatusTitle = (category?: string | string[]) => {
  if (!category) {
    return 'Status';
  }

  const firstCategory = Array.isArray(category) ? category[0] : category;
  if (!firstCategory) {
    return 'Status';
  }

  const formattedCategory = formatCategoryName(firstCategory);

  return `${formattedCategory} Status`;
};

interface CmailStatusBannerProps {
  readonly missingFieldsCount: number;
  readonly failureType?: string;
  readonly trackingData?: RequestTrackingData | null;
  readonly warningsCount: number;
  readonly onClick: () => void;
  readonly jiraTicketNumber?: string;
  readonly category?: string | string[];
}

export const CmailStatusBanner: React.FC<CmailStatusBannerProps> = ({
  missingFieldsCount,
  failureType,
  trackingData,
  warningsCount,
  onClick,
  jiraTicketNumber,
  category,
}) => {
  const getPriorityLevel = () => {
    if (failureType) return 'error';
    if (trackingData && trackingData.failedItems > 0) return 'error';
    if (missingFieldsCount > 0) return 'warning';
    if (trackingData && trackingData.escalatedItems > 0) return 'warning';
    return 'info';
  };

  const getIcon = () => {
    const priority = getPriorityLevel();
    if (priority === 'error') {
      return (
        <FontAwesomeIcon
          icon={faCircleExclamation}
          className={classes.statusIcon}
        />
      );
    }
    if (priority === 'warning') {
      return (
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className={classes.statusIcon}
        />
      );
    }
    return (
      <FontAwesomeIcon icon={faCircleInfo} className={classes.statusIcon} />
    );
  };

  const getTitle = () => {
    const priority = getPriorityLevel();
    if (priority === 'error') {
      if (trackingData && trackingData.failedItems > 0 && !failureType) {
        return 'Request Tracking Failed';
      }
      return 'Processing Error';
    }
    if (priority === 'warning') {
      if (
        trackingData &&
        trackingData.escalatedItems > 0 &&
        missingFieldsCount === 0
      ) {
        return 'Items Escalated';
      }
      return getStatusTitle(category);
    }
    return getStatusTitle(category);
  };

  const getDescription = () => {
    const items: string[] = [];

    if (failureType) {
      items.push('Processing error');
    }
    if (missingFieldsCount > 0) {
      items.push(
        `${missingFieldsCount} missing field${
          missingFieldsCount !== 1 ? 's' : ''
        }`
      );
    }
    if (trackingData && trackingData.totalItems > 0) {
      const parts: string[] = [];
      parts.push(
        `${trackingData.totalItems} item${
          trackingData.totalItems !== 1 ? 's' : ''
        } tracked`
      );

      if (trackingData.failedItems > 0) {
        parts.push(`${trackingData.failedItems} failed`);
      }
      if (trackingData.escalatedItems > 0) {
        parts.push(`${trackingData.escalatedItems} escalated`);
      }

      items.push(parts.join(', '));
    }
    if (warningsCount > 0) {
      items.push(`${warningsCount} warning${warningsCount !== 1 ? 's' : ''}`);
    }

    return items.join(' • ');
  };

  const note = escalationNote(trackingData);

  return (
    <Box
      className={classes.banner}
      data-priority={getPriorityLevel()}
      onClick={onClick}
    >
      <Flex align="center" gap="sm" className={classes.bannerFlex}>
        {getIcon()}
        <Box className={classes.bannerContent}>
          <Text fw={700} size="md" className={classes.bannerTitle}>
            {getTitle()}
          </Text>
          <Text size="sm" className={classes.bannerDescription}>
            {getDescription()}
          </Text>
          {note && (
            <Text size="sm" className={classes.bannerNote}>
              {note}
            </Text>
          )}
        </Box>
      </Flex>

      {jiraTicketNumber && (
        <Flex align="center">
          <Anchor
            href={`${JIRA_BASE_URL}/${jiraTicketNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.jiraLink}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
          >
            {jiraTicketNumber}
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              className={classes.jiraExternalIcon}
            />
          </Anchor>
        </Flex>
      )}
    </Box>
  );
};
